import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

// Disable auth toasts on overlay page
const originalError = console.error;
console.error = (...args) => {
  if (args[0]?.includes?.('Auth check failed') || args[0]?.includes?.('Session expired')) {
    return; // Suppress auth errors on overlay page
  }
  originalError.apply(console, args);
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

export default function StreamOverlay() {
  const { username } = useParams();
  // Determine if current viewer is the streamer owner with pro rights
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = !!(
    token &&
    (user?.username?.toLowerCase?.() === username?.toLowerCase?.()) &&
    (
      user?.membershipPlan?.toLowerCase?.() === 'pro' ||
      user?.role === 'creator_pro' ||
      user?.isPro === true
    )
  );
  const [cards, setCards] = useState([]);
  const processedCardIdsRef = useRef(new Set());
  const [processedCardIds, setProcessedCardIds] = useState(new Set());
  const initialCardIdsRef = useRef(null);
  const [lastProcessedCard, setLastProcessedCard] = useState(null);
  const [currentCard, setCurrentCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [hasEngaged, setHasEngaged] = useState(false);
  const [displayedCards, setDisplayedCards] = useState(new Set());
  const popupTimerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const visibleAtRef = useRef(0);
  const hasEngagedRef = useRef(false);
  const lastSeenRef = useRef(0);

  // Audio setup
  const ensureAudio = () => {
    if (!audioCtxRef.current) {
      try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch { }
    }
    return audioCtxRef.current;
  };

  const tryResumeAudio = async () => {
    const ctx = ensureAudio();
    if (!ctx) return false;
    try {
      if (ctx.state === 'suspended' && ctx.resume) {
        await ctx.resume();
      }
      audioUnlockedRef.current = true;
      return ctx.state === 'running';
    } catch {
      return false;
    }
  };

  const playTone = (freq = 880, durationMs = 140, type = 'sine', gain = 0.12) => {
    const ctx = ensureAudio();
    if (!ctx) return;
    try { if (ctx.state === 'suspended' && ctx.resume) ctx.resume(); } catch { }
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.value = 0;
    o.connect(g);
    g.connect(ctx.destination);
    const now = ctx.currentTime;
    g.gain.linearRampToValueAtTime(gain, now + 0.01);
    g.gain.linearRampToValueAtTime(0.0001, now + durationMs / 1000);
    o.start(now);
    o.stop(now + durationMs / 1000 + 0.02);
  };

  const playAppearSound = () => {
    console.log('🔊 Playing appear sound');
    playTone(660, 90, 'sine', 0.08);
    setTimeout(() => playTone(990, 100, 'triangle', 0.08), 80);
    setTimeout(() => playTone(1320, 110, 'triangle', 0.06), 150);
  };

  const playAcceptSound = () => {
    playTone(880, 120, 'triangle', 0.12);
    setTimeout(() => playTone(1175, 140, 'triangle', 0.12), 110);
  };

  const playLaterSound = () => {
    playTone(320, 90, 'sine', 0.10);
    setTimeout(() => playTone(220, 80, 'sine', 0.08), 70);
  };

  // Load processed cards and capture initial state on mount
  useEffect(() => {
    if (!username) return;

    const loadInitialCards = async () => {
      try {
        // Load processed cards from localStorage
        const stored = localStorage.getItem(`processedCards_${username}`);
        if (stored) {
          const ids = JSON.parse(stored);
          const idsSet = new Set(ids);
          processedCardIdsRef.current = idsSet;
          setProcessedCardIds(idsSet);
          console.log('📂 Loaded processed cards from storage:', ids.length);
        } else {
          console.log('📂 No processed cards in storage, starting fresh');
          processedCardIdsRef.current = new Set();
        }

        // Fetch current cards and mark them all as "already existed"
        try {
          const response = await axios.get(`${API_BASE_URL}/api/creators/${username}/purchased-cards`);
          if (response.data.status === 'success') {
            const existingCards = response.data.data || [];
            const existingIds = existingCards.map(c => c._id).filter(Boolean);
            initialCardIdsRef.current = new Set(existingIds);
            console.log('🎬 Initial cards captured:', existingIds.length, '- these will NOT be shown');
          }
        } catch (e) {
          console.error('Error fetching initial cards:', e);
          initialCardIdsRef.current = new Set();
        }
      } catch (e) {
        console.error('Error loading processed cards:', e);
        processedCardIdsRef.current = new Set();
        setProcessedCardIds(new Set());
        initialCardIdsRef.current = new Set();
      }
    };

    loadInitialCards();
  }, [username]);

  // Set up polling for new cards
  useEffect(() => {
    if (!username) return;

    let isMounted = true;
    let pollCount = 0;

    const fetchCards = async () => {
      if (!isMounted) return;
      pollCount++;
      console.log(`🔄 Poll #${pollCount}`);

      try {
        await fetchAndProcessCards();
      } catch (error) {
        console.error('Error in card polling:', error);
      }
    };

    // Initial fetch after 1 second
    const initialTimeout = setTimeout(fetchCards, 1000);

    // Set up polling every 5 seconds
    const interval = setInterval(fetchCards, 5000);

    // Cleanup on unmount
    return () => {
      isMounted = false;
      clearTimeout(initialTimeout);
      clearInterval(interval);
      if (popupTimerRef.current) {
        clearTimeout(popupTimerRef.current);
      }
    };
  }, [username]); // Only re-run when username changes

  // Unlock audio on user interaction
  useEffect(() => {
    const unlock = async () => {
      const ctx = ensureAudio();
      if (!ctx || audioUnlockedRef.current) return;
      try {
        if (ctx.state === 'suspended') await ctx.resume();
        audioUnlockedRef.current = true;
      } catch { }
    };
    const onInteraction = () => unlock();
    window.addEventListener('pointerdown', onInteraction, { once: true });
    window.addEventListener('mousemove', onInteraction, { once: true });
    window.addEventListener('keydown', onInteraction, { once: true });
    return () => {
      window.removeEventListener('pointerdown', onInteraction);
      window.removeEventListener('mousemove', onInteraction);
      window.removeEventListener('keydown', onInteraction);
    };
  }, []);

  // Save timeout card to localStorage
  const saveTimeoutCard = (card) => {
    try {
      const timeoutCards = JSON.parse(localStorage.getItem('timeoutCards') || '[]');
      const newTimeoutCard = {
        _id: `timeout_${card._id}_${Date.now()}`,
        cardId: card,
        status: 'timeout',
        createdAt: new Date().toISOString(),
        title: card.cardTitle || 'Timeout Card',
        description: card.description || ''
      };

      const fewSecondsAgo = Date.now() - 3 * 1000;
      const exists = timeoutCards.some(tc => {
        const tcTime = new Date(tc.createdAt).getTime();
        return tc.cardId?._id === card._id && tcTime > fewSecondsAgo;
      });

      if (!exists) {
        timeoutCards.push(newTimeoutCard);
        localStorage.setItem('timeoutCards', JSON.stringify(timeoutCards));
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'timeoutCards',
          newValue: JSON.stringify(timeoutCards)
        }));
      }
    } catch (e) {
      console.error('Failed to save timeout card:', e);
    }
  };

  // Fetch BOTH broadcast cards AND purchased cards AND admin challenges
  const fetchAndProcessCards = async () => {
    if (!username) {
      console.log('⚠️ No username provided, skipping fetch');
      return;
    }
    // Wait until initial cards set is captured to avoid showing baseline backlog
    if (initialCardIdsRef.current === null) {
      console.log('⏳ Waiting for initial cards capture before polling...');
      return;
    }

    // Skip if we're already showing a popup
    if (showPopup || window.__overlayPopupActive) {
      console.log('⏸️ Popup already active, skipping fetch');
      return;
    }

    try {
      console.log('🔍 Fetching cards for username:', username);

      // 1. Fetch purchased cards
      let purchasedCards = [];
      try {
        const purchasedResponse = await axios.get(`${API_BASE_URL}/api/creators/${username}/purchased-cards`);
        const rawPurchasedCards = (purchasedResponse.data.status === 'success' ? purchasedResponse.data.data : []) || [];

        purchasedCards = rawPurchasedCards.map(card => {
          const eventTs = card.sentAt || card.updatedAt || card.purchaseDate || card.createdAt;
          return {
            ...card,
            isPurchased: true,
            isBroadcast: false,
            isAdminChallenge: false,
            isFanSent: card.isFanSent || false,
            cardTitle: card.cardTitle || card.cardId?.title || 'Untitled',
            description: card.description || card.cardId?.description || '',
            imageUrl: card.imageUrl || card.cardId?.imageUrl || '',
            rarity: card.rarity || card.cardId?.rarity || 'common',
            category: card.category || card.cardId?.category || '',
            createdAt: eventTs
          };
        });
      } catch (e) {
        console.error('Error fetching purchased cards:', e);
      }

      // 2. Fetch admin challenges
      let adminChallenges = [];
      try {
        const adminResponse = await axios.get(`${API_BASE_URL}/api/admin/challenges/streamer/${username}`);
        if (adminResponse.data.status === 'success') {
          const rawAdminChallenges = adminResponse.data.data || [];
          adminChallenges = rawAdminChallenges.map(challenge => ({
            _id: challenge._id,
            isPurchased: false,
            isBroadcast: false,
            isAdminChallenge: true,
            cardTitle: challenge.cardId?.cardTitle || 'Unknown Card',
            description: challenge.message || challenge.cardId?.description || '',
            imageUrl: challenge.cardId?.imageUrl || '',
            rarity: challenge.cardId?.rarity || 'common',
            category: challenge.cardId?.category || [],
            createdAt: challenge.sentAt || challenge.createdAt,
            sentAt: challenge.sentAt,
            actionStatus: challenge.actionStatus,
            buyerUsername: 'Crezar',
            tag: 'CREZAR'
          }));
        }
      } catch (e) {
        console.error('Error fetching admin challenges:', e);
      }

      // Combine all cards
      const all = [...purchasedCards, ...adminChallenges];
      console.log('🃏 Total cards received:', all.length);

      // Get currently processed cards from ref
      const currentProcessed = processedCardIdsRef.current;
      const initialCards = initialCardIdsRef.current || new Set();

      // Filter to ONLY cards that:
      // 1. Are NOT in processed list
      // 2. Were NOT present when page loaded (truly NEW)
      const nowMs = Date.now();
      const newCards = all.filter(card => {
        const id = card._id;
        if (!id) return false;
        const isProcessed = currentProcessed.has(id);
        const wasInitial = initialCards.has(id);
        const sentMs = card.sentAt ? new Date(card.sentAt).getTime() : 0;
        const createdMs = new Date(card.createdAt || 0).getTime();

        // Treat recent sends as new
        const isRecent = (nowMs - sentMs < 60_000) || (nowMs - createdMs < 90000);

        if (!isRecent && !isProcessed) {
          // Add to processed list so we don't check it again
          processedCardIdsRef.current.add(id);
          return false;
        }

        // treat very recent events as new even if underlying transaction existed at load
        return !isProcessed && (!wasInitial || isRecent);
      });

      console.log('🆕 NEW cards (not present on load):', newCards.length, 'out of', all.length);

      // Sort by newest first
      const sorted = [...newCards].sort((a, b) =>
        new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );

      // Update cards list
      setCards(sorted);

      const unprocessedCards = sorted;

      if (unprocessedCards.length > 0 && !showPopup && !window.__overlayPopupActive) {
        const cardToShow = unprocessedCards[0];
        console.log('✨ Preparing to show card:', cardToShow._id, cardToShow.cardTitle);

        try {
          // Set current card and show popup
          setCurrentCard(cardToShow);
          setShowPopup(true);
          window.__overlayPopupActive = true;
          setHasEngaged(false);
          hasEngagedRef.current = false;
          visibleAtRef.current = Date.now();

          // Play sound for all card types
          playAppearSound();

          // Mark as processed immediately in ref and localStorage
          processedCardIdsRef.current.add(cardToShow._id);

          // Save to localStorage
          localStorage.setItem(
            `processedCards_${username}`,
            JSON.stringify(Array.from(processedCardIdsRef.current))
          );

          console.log('✅ Card marked as processed:', cardToShow._id);

          // Auto-dismiss after 15 seconds if no action taken
          clearTimeout(popupTimerRef.current);
          popupTimerRef.current = setTimeout(() => {
            console.log('⏰ Auto-dismiss timer fired');
            if (!hasEngagedRef.current) {
              console.log('⏰ Auto-dismissing card after timeout');
              dismissPopup(true);
            }
          }, 15000);

          console.log('⏱️ Auto-dismiss timer set for 15 seconds');

        } catch (e) {
          console.error('Error processing card:', e);
        }
      }

    } catch (err) {
      console.error('❌ Error in fetchAndProcessCards:', err);
    } finally {
      setLoading(false);
      try {
        const key = `overlay_last_seen_${username}`;
        const stored = localStorage.getItem(key);
        lastSeenRef.current = stored ? Number(stored) : Date.now();
        if (!stored) localStorage.setItem(key, String(lastSeenRef.current));
      } catch (e) {
        console.error('Error accessing localStorage:', e);
      }
    }
  };

  // Later handler
  const handleLater = async () => {
    if (!isOwner) {
      console.warn('Viewer is not the owner; Later action disabled');
      return;
    }
    if (!currentCard || hasEngaged) return;
    console.log('⏳ Card snoozed:', currentCard.cardTitle);
    setHasEngaged(true);
    hasEngagedRef.current = true;
    playLaterSound();

    // Save to backend first
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userEmail = user?.emailId || user?.username || user;

      if (token && userEmail && isOwner) {
        if (currentCard.isBroadcast) {
          // Broadcast card - use broadcast later endpoint
          console.log('📡 Saving broadcast card snooze, card ID:', currentCard._id);
          await axios.post(
            `${API_BASE_URL}/auth/user/broadcast-cards/${currentCard._id}/later`,
            { snoozeMinutes: 30 },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'User-Info': userEmail,
              },
            }
          );
        } else if (currentCard.isAdminChallenge) {
          // Admin challenge - use admin challenge action endpoint
          console.log('👑 Saving admin challenge snooze, ID:', currentCard._id);
          await axios.post(
            `${API_BASE_URL}/api/admin/challenges/${currentCard._id}/action`,
            { action: 'snoozed' },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'User-Info': userEmail,
              },
            }
          );
        } else if (currentCard.isPurchased) {
          // Purchased card - use transaction action endpoint
          console.log('💳 Saving purchased card snooze, transaction ID:', currentCard._id);
          await axios.post(
            `${API_BASE_URL}/auth/user/purchased-cards/${currentCard._id}/action`,
            { action: 'snoozed' },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'User-Info': userEmail,
              },
            }
          );
        }
        console.log('💾 Card snoozed saved to backend');
      }
    } catch (e) {
      console.error('Error saving snooze to backend:', e);
    }

    try {
      const laterCards = JSON.parse(localStorage.getItem('laterCards') || '[]');

      // Remove any existing entries for this card to prevent duplicates
      const filteredCards = laterCards.filter(lc =>
        (lc.cardId?._id || lc._id) !== currentCard._id
      );

      const laterCard = {
        _id: `later_${currentCard._id}_${Date.now()}`,
        cardId: {
          _id: currentCard.cardId || currentCard._id,
          title: currentCard.cardTitle,
          description: currentCard.description,
          imageUrl: currentCard.imageUrl,
          rarity: currentCard.rarity,
          category: currentCard.category
        },
        status: 'snoozed',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24h from now
      };

      filteredCards.push(laterCard);
      localStorage.setItem('laterCards', JSON.stringify(filteredCards));

      // Notify other tabs
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'laterCards',
        newValue: JSON.stringify(filteredCards)
      }));

      // Dismiss immediately since we've saved the state
      dismissPopup(false);
    } catch (e) {
      console.error('Error saving to later cards:', e);
      dismissPopup(false);
    }
  };

  // Handle accept button click
  const handleAccept = async () => {
    if (!isOwner) {
      console.warn('Viewer is not the owner; Accept action disabled');
      return;
    }
    if (!currentCard || hasEngaged || !currentCard._id) {
      console.log('⚠️ Cannot accept card:', { currentCard, hasEngaged });
      return;
    }
    console.log('✅ Card accepted:', currentCard._id, currentCard.cardTitle);
    setHasEngaged(true);
    hasEngagedRef.current = true;
    playAcceptSound();

    // Save to backend
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userEmail = user?.emailId || user?.username || user;

      if (token && userEmail) {
        if (currentCard.isBroadcast) {
          // Broadcast card - use broadcast engagement endpoint
          await axios.post(
            `${API_BASE_URL}/auth/user/broadcast-cards/${currentCard._id}/engage`,
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'User-Info': userEmail,
              },
            }
          );
        } else if (currentCard.isAdminChallenge) {
          // Admin challenge - use admin challenge action endpoint
          await axios.post(
            `${API_BASE_URL}/api/admin/challenges/${currentCard._id}/action`,
            { action: 'accepted' },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'User-Info': userEmail,
              },
            }
          );
        } else if (currentCard.isPurchased) {
          // Purchased card - use transaction action endpoint
          console.log('💳 Saving purchased card action, transaction ID:', currentCard._id);
          await axios.post(
            `${API_BASE_URL}/auth/user/purchased-cards/${currentCard._id}/action`,
            { action: 'accepted' },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'User-Info': userEmail,
              },
            }
          );
        }
        console.log('💾 Card acceptance saved to backend');
      }
    } catch (e) {
      console.error('Error saving to backend:', e);
    }

    // Mark as processed in ref and localStorage
    try {
      processedCardIdsRef.current.add(currentCard._id);
      localStorage.setItem(
        `processedCards_${username}`,
        JSON.stringify(Array.from(processedCardIdsRef.current))
      );
      setProcessedCardIds(new Set(processedCardIdsRef.current));
      console.log('📝 Updated processed cards:', Array.from(processedCardIdsRef.current));
    } catch (e) {
      console.error('Error processing card acceptance:', e);
    }

    // Dismiss the popup after a short delay
    setTimeout(() => {
      setShowPopup(false);
      setCurrentCard(null);
      window.__overlayPopupActive = false;
      hasEngagedRef.current = false;
      setHasEngaged(false);
      console.log('✅ Popup dismissed, ready for next card');
    }, 500);
  };

  // Dismiss popup handler
  const dismissPopup = async (autoDismiss = false) => {
    console.log('👋 Dismissing popup, autoDismiss:', autoDismiss);

    if (popupTimerRef.current) {
      clearTimeout(popupTimerRef.current);
      popupTimerRef.current = null;
    }

    if (currentCard) {
      console.log('📤 Dismissing card:', currentCard._id);

      // Add to displayed cards to prevent showing again
      setDisplayedCards(prev => {
        const updated = new Set(prev);
        updated.add(currentCard._id);
        return updated;
      });

      // Mark as processed in ref, state and localStorage
      try {
        processedCardIdsRef.current.add(currentCard._id);

        localStorage.setItem(
          `processedCards_${username}`,
          JSON.stringify(Array.from(processedCardIdsRef.current))
        );

        setProcessedCardIds(new Set(processedCardIdsRef.current));
        console.log('💾 Card marked as processed in dismissPopup:', currentCard._id, 'Total:', processedCardIdsRef.current.size);
      } catch (e) {
        console.error('Error updating processed cards:', e);
      }

      if (autoDismiss) {
        console.log('⏰ Card auto-dismissed (timeout)');
        console.log('🔍 Current card details:', JSON.stringify({
          id: currentCard._id,
          isPurchased: currentCard.isPurchased,
          isBroadcast: currentCard.isBroadcast,
          isFanSent: currentCard.isFanSent,
          title: currentCard.cardTitle
        }, null, 2));

        // CRITICAL FIX: Save ALL purchased/fan-sent cards on auto-dismiss
        // Check if this is a purchased card (from streamer page or sent by fan)
        const shouldSaveToBackend = currentCard.isPurchased || currentCard.isFanSent || !currentCard.isBroadcast;

        if (shouldSaveToBackend) {
          try {
            const token = localStorage.getItem('token');
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const userEmail = user?.emailId || user?.username || user;

            if (token && userEmail) {
              console.log('💾 AUTO-DISMISS: Saving card as pending, ID:', currentCard._id);

              const response = await axios.post(
                `${API_BASE_URL}/auth/user/purchased-cards/${currentCard._id}/action`,
                { action: 'pending' },
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'User-Info': userEmail,
                  },
                }
              );

              console.log('✅ AUTO-DISMISS SAVED:', response.data);
              toast.success('Card auto-dismissed and saved!', { autoClose: 2000 });
            } else {
              console.error('❌ Missing token or user email for auto-dismiss save');
              toast.error('Cannot save: not logged in');
            }
          } catch (e) {
            console.error('❌ Error saving auto-dismissed card:', e);
            console.error('❌ Error response:', e.response?.data);
            toast.error(`Failed to save: ${e.response?.data?.error || e.message}`);
          }
        } else {
          console.log('ℹ️ Card is broadcast only, skipping backend save');
        }
      }
    }

    // Clear popup state
    setShowPopup(false);
    setCurrentCard(null);
    window.__overlayPopupActive = false;
    hasEngagedRef.current = false;
    setHasEngaged(false);

    console.log('✅ Popup dismissed, ready for next card');
  };

  // Dismiss handler (for close button)
  const handleDismiss = () => dismissPopup(false);

  const getRarityColor = (rarity) => {
    switch (rarity?.toLowerCase()) {
      case 'common':
        return 'from-gray-600 to-gray-800';
      case 'rare':
        return 'from-blue-600 to-blue-800';
      case 'epic':
        return 'from-purple-600 to-purple-800';
      case 'legendary':
        return 'from-orange-600 to-orange-800';
      default:
        return 'from-gray-600 to-gray-800';
    }
  };

  const getRarityGlow = (rarity) => {
    switch (rarity?.toLowerCase()) {
      case 'common':
        return 'shadow-gray-500/50';
      case 'rare':
        return 'shadow-blue-500/50';
      case 'epic':
        return 'shadow-purple-500/50';
      case 'legendary':
        return 'shadow-orange-500/50';
      default:
        return 'shadow-gray-500/50';
    }
  };

  // Don't show loading or empty state - just render transparent background
  // Cards will popup when purchased

  return (
    <div className="w-screen h-screen overflow-hidden relative" style={{ background: '#00ff00' }}>
      {/* Popup card overlay - only shows when there's a new purchase */}
      {showPopup && currentCard && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          {/* Semi-transparent backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>

          {/* Card popup */}
          <div className="relative z-10 w-full max-w-sm px-4 pointer-events-auto animate-slideIn">
            <div
              className={`bg-gradient-to-br ${getRarityColor(currentCard.rarity)} rounded-2xl p-4 shadow-2xl ${getRarityGlow(currentCard.rarity)} transform transition-all duration-500 relative`}
            >
              {/* Close button */}
              <button
                onClick={handleDismiss}
                className="absolute top-2 right-2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors z-10"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>



              {/* Card Image */}
              {currentCard.imageUrl && (
                <div className="relative mb-3 rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={currentCard.imageUrl}
                    alt={currentCard.cardTitle}
                    className="w-full h-auto aspect-[2/3] object-cover"
                    onError={(e) => {
                      console.error('Image failed to load:', currentCard.imageUrl);
                      e.target.style.display = 'none';
                    }}
                  />

                </div>
              )}

              {/* Card Info */}
              <div className="text-center mb-4">
                {!currentCard.isAdminChallenge && (
                  <h3 className="text-xl font-bold text-white mb-1 drop-shadow-md">{currentCard.cardTitle}</h3>
                )}
                <div className="flex items-center justify-center gap-2">
                  {/* Hide CREZAR tag and admin text for admin challenges */}
                  {currentCard.tag && !currentCard.isAdminChallenge && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-500 text-black border border-yellow-400">
                      {currentCard.tag}
                    </span>
                  )}
                  {!currentCard.isAdminChallenge && (
                    <p className="text-white/90 text-sm font-medium drop-shadow-sm">
                      {currentCard.isFanSent ? 'Gifted by Fan' : `Purchased by ${currentCard.buyerUsername || 'Fan'}`}
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={handleAccept}
                  disabled={!isOwner || hasEngaged}
                  className={`flex-1 py-2.5 px-4 sm:px-5 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 ${(!isOwner || hasEngaged)
                    ? 'bg-green-500 text-white cursor-not-allowed'
                    : 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700 transform hover:scale-105'
                    }`}
                >
                  {hasEngaged ? (
                    <span className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Accepted
                    </span>
                  ) : (
                    'Accept Challenge'
                  )}
                </button>

                <button
                  onClick={handleLater}
                  disabled={!isOwner}
                  className={`px-4 sm:px-5 py-2.5 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 transform ${!isOwner ? 'bg-white/20 text-white/60 cursor-not-allowed' : 'bg-white/20 hover:bg-white/30 text-white hover:scale-105'
                    }`}
                >
                  Later
                </button>
              </div>

              {/* Progress bar showing 15 second countdown */}
              <div className="mt-3">
                <div className="h-1 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-yellow-400 animate-progress"
                    style={{ animationDuration: '15s' }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Creator watermark */}
            <div className="text-center mt-6">
              <p className="text-white/80 text-sm drop-shadow-lg">
                Stream by <span className="text-yellow-400 font-semibold">{username}</span>
                {!isOwner && (
                  <span className="ml-2 text-white/60">(viewer mode – controls disabled)</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Debug panel - shows API status */}
      <div className="fixed bottom-4 right-4 bg-black/90 backdrop-blur-sm text-white px-4 py-3 rounded-lg text-xs font-mono max-w-sm">
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${loading ? 'bg-yellow-400' : 'bg-green-400'} animate-pulse`}></div>
            <span className="font-semibold">Stream Overlay Active</span>
          </div>
          <div className="text-gray-300">Username: {username}</div>
          <div className="text-gray-300">Recent(24h): {cards.length}</div>
          <div className="text-gray-300">Undisplayed: {cards.filter(c => !displayedCards.has(c._id)).length}</div>
          <div className="text-gray-300">Current: {currentCard ? currentCard.cardTitle : 'None'}</div>
          <div className="text-gray-300">Popup: {showPopup ? 'Visible' : 'Hidden'}</div>
          {error && <div className="text-red-400">Error: {error}</div>}
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }

        @keyframes progress {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }

        .animate-slideIn {
          animation: slideIn 1s ease-out;
        }

        .animate-float {
          animation: float linear infinite;
        }

        .animate-progress {
          animation: progress linear;
        }
      `}</style>
    </div>
  );
}
